import { createMediaQueryList } from '../utils/_breakpoints';
import { getDataProperty } from '../utils/_domFunctions';

class mPdVideoScroll extends HTMLDivElement {
    constructor() {
        super();
        this.mqlDesktop = createMediaQueryList('md');
        this.intersectTopObserver = null;
        this.intersectBottomObserver = null;
        this.layerEl = null;
        this.videoMain = this.querySelector('.video-main');
        this.videoMobile = this.querySelector('.video-mobile');
        this.srcMain = getDataProperty(this, 'srcMain', 'string');
        if (!this.srcMain) {
            throw new Error('Missing main video source');
        }
        this.srcMobile = getDataProperty(this, 'srcMobile', 'string', this.srcMain);
        this.currentVideo = null;
        this.videoSeeked = false;

        this.updateVideoTimeLoop = this.updateVideoTimeLoop.bind(this);
    }

    connectedCallback() {
        this.layerEl = this.querySelector('.layer');
        if (!this.layerEl) {
            throw new Error('Missing layer element');
        }

        // Setup videos
        if (!this.videoMain) {
            throw new Error('Missing main video element');
        }
        this.videoMain.addEventListener('seeked', () => {
            if (this.videoMain === this.currentVideo) {
                this.videoSeeked = true;
            }
        });
        if (this.videoMobile) {
            this.videoMobile.addEventListener('seeked', () => {
                if (this.videoMobile === this.currentVideo) {
                    this.videoSeeked = true;
                }
            });
        }
        this.updateResponsive();
        this.mqlDesktop.addEventListener('change', this.updateResponsive.bind(this));

        // Setup top threshold trigger
        this.intersectTopObserver = new IntersectionObserver(
            this.onIntersectTop.bind(this),
            {
                root: document, // Needed to work in an iframe
                rootMargin: '0px 0px -100% 0px'
            }
        );
        this.intersectTopObserver.observe(this);

        // Setup bottom threshold trigger
        this.intersectBottomObserver = new IntersectionObserver(
            this.onIntersectBottom.bind(this),
            {
                root: document, // Needed to work in an iframe
                rootMargin: '-100% 0px 0px 0px'
            }
        );
        this.intersectBottomObserver.observe(this);

        // Starting video scroll sync loop
        this.updateVideoTimeLoop();
    }

    disconnectedCallback() {
        this.intersectBottomObserver.disconnect();
    }

    updateResponsive() {
        const isDesktop = this.mqlDesktop.matches;
        let newVideo;

        if (!isDesktop && this.videoMobile) {
            newVideo = this.videoMobile;
            this.videoMobile.src = this.srcMobile;
        } else {
            newVideo = this.videoMain;
            this.videoMain.src = this.srcMain;
        }
        if (newVideo !== this.currentVideo) {
            if (this.currentVideo) {
                this.currentVideo.pause();
            }
            this.currentVideo = newVideo;
            // iOS Safari: need to play the video once to be able to use the currentTime property
            newVideo.play()
                .then(() => {
                    newVideo.pause();
                    newVideo.currentTime = 0;
                })
                .catch((e) => {
                    // eslint-disable-next-line no-console
                    console.error(`${this.constructor.name}: Cannot play video "${newVideo.src}": ${e.message}`);
                });
        }
    }

    onIntersectTop(entries) {
        const entry = entries[0];
        if (entry.isIntersecting) {
            this.layerEl.classList.remove('top-lock');
        } else {
            this.layerEl.classList.add('top-lock');
        }
    }

    onIntersectBottom(entries) {
        const entry = entries[0];
        if (entry.isIntersecting) {
            this.layerEl.classList.remove('bottom-lock');
        } else {
            this.layerEl.classList.add('bottom-lock');
        }
    }

    // Note: Using a requestAnimationFrame loop give a better framerate than
    // listening for a scroll event before updating
    updateVideoTimeLoop() {
        window.requestAnimationFrame(this.updateVideoTimeLoop);
        if (!this.videoSeeked || isNaN(this.currentVideo.duration)) {
            return;
        }
        this.videoSeeked = false;
        const layerHeight = this.layerEl.getBoundingClientRect().height;
        const containerRect = this.getBoundingClientRect();
        const maxScroll = containerRect.height - layerHeight;
        const scrollProgress = -containerRect.y / Math.max(maxScroll, 1);
        const progress = Math.max(0, Math.min(Math.round(scrollProgress * 100) / 100, 1));
        this.currentVideo.currentTime = (this.currentVideo.duration - 0.1) * progress;
    }
}

customElements.define('m-pd-video-scroll', mPdVideoScroll, {
    extends: 'div'
});
